import { ChangeEvent, useEffect, useState } from "react";
import CustomInput from "../CustomInput";
import closeIcon from "../../assests/close.svg";
import CustomButton from "../CustomButton";
import { toast } from "react-toastify";
import { ParseErrors } from "../../utils/ParseErrors";
import axiosInstance from "../../utils/axiosInstance";
import Loader from "../Loader";
import { User } from "../../providers/AuthProvider";
import GetUser from "../../utils/getUser";

interface TCreateUserModal {
  ToggleModel: () => void;
  isEdit: User | boolean;
  onSuccess?: () => void;
}

const userTypes = [
  { label: "Admin", value: "admin" },
  { label: "Agent", value: "agent" },
];

const CreateAgentModel = ({ ToggleModel, isEdit, onSuccess }: TCreateUserModal) => {
  const currentUser = GetUser();
  const [isLoading, setIsLoading] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({ type: "agent" });
  const isUpdate = typeof isEdit !== "boolean" && isEdit?.id;

  useEffect(() => {
    if (isUpdate) {
      setValues({
        name: isEdit.name || "",
        email: isEdit.email || "",
        phone: isEdit.phone || "",
        code: isEdit.code || "",
        type: isEdit.type || "agent",
      });
    } else {
      setValues({ type: "agent" });
    }
  }, [isEdit, isUpdate]);

  const createUser = async () => {
    setIsLoading(true);
    try {
      const res = await axiosInstance.post("/auth/register", values);
      toast.success(res.data.message);
      onSuccess?.();
      ToggleModel();
    } catch (error) {
      toast.error(ParseErrors(error));
    } finally {
      setIsLoading(false);
    }
  };

  const updateUser = async () => {
    setIsLoading(true);
    try {
      const res = await axiosInstance.put(
        `/user/update/${isUpdate && isEdit.id}`,
        values,
      );
      toast.success(res.data.message);
      onSuccess?.();
      ToggleModel();
    } catch (error) {
      toast.error(ParseErrors(error));
    } finally {
      setIsLoading(false);
    }
  };

  const onChangeValues = (e: ChangeEvent<HTMLInputElement>) => {
    setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const roleOptions =
    currentUser.type === "super-admin"
      ? [{ label: "Super Admin", value: "super-admin" }, ...userTypes]
      : userTypes;

  return (
    <div
      className="fixed inset-0 z-[99999] flex h-full w-full items-center justify-center overflow-y-auto bg-[rgba(0,0,0,0.5)] p-3 sm:p-4"
      onClick={ToggleModel}
    >
      <div
        className="w-full max-w-[480px] rounded-2xl border border-edge bg-surface shadow-overlay"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-edge px-4 py-4 sm:px-6">
          <h2 className="font-[Gilroy-Bold] text-[20px] text-heading-color sm:text-[22px]">
            {isUpdate ? "Edit User" : "Invite User"}
          </h2>
          <button type="button" onClick={ToggleModel}>
            <img
              src={closeIcon}
              alt="Close"
              className="h-8 w-8 cursor-pointer"
            />
          </button>
        </div>

        <div className="max-h-[calc(100vh-8rem)] space-y-4 overflow-y-auto p-4 sm:p-6">
          <CustomInput
            autoComplete="off"
            label="Full Name"
            value={values.name || ""}
            name="name"
            onChange={onChangeValues}
          />
          <CustomInput
            autoComplete="off"
            label="Email"
            value={values.email || ""}
            name="email"
            onChange={onChangeValues}
          />
          <CustomInput
            autoComplete="off"
            label="Phone"
            value={values.phone || ""}
            name="phone"
            onChange={onChangeValues}
          />
          <CustomInput
            autoComplete="off"
            label="User Code"
            value={values.code || ""}
            name="code"
            onChange={onChangeValues}
          />

          {!isUpdate && (
            <CustomInput
              autoComplete="off"
              label="Temporary Password"
              name="password"
              type="password"
              onChange={onChangeValues}
            />
          )}

          <div>
            <label className="mb-2 block font-[Gilroy-Medium] text-sm text-light-gray">
              User Role
            </label>
            <select
              value={values.type || "agent"}
              onChange={(e) =>
                setValues((prev) => ({ ...prev, type: e.target.value }))
              }
              className="h-[40px] w-full rounded border border-edge bg-input-bg px-4 font-[Gilroy-Medium] text-[14px] text-heading-color outline-none focus:border-accent"
            >
              {roleOptions.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {!isUpdate && (
            <p className="font-[Gilroy-Medium] text-[12px] text-label-muted">
              An invite email will be sent so the user can set their password
              and log in.
            </p>
          )}

          <CustomButton
            disabled={isLoading}
            fullWidth
            variant="primary"
            className="!rounded-xl !font-[Gilroy-Medium]"
            onClick={isUpdate ? updateUser : createUser}
          >
            {isLoading ? (
              <Loader />
            ) : isUpdate ? (
              "Save Changes"
            ) : (
              "Send Invite"
            )}
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

export default CreateAgentModel;
